import { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { DataFieldsEnum } from 'src/shared/models/enums/dataFields.enum';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';

import { getMainSettings, updateMainSettings, getOnboardingSettings, updateOnboardingSettings } from 'src/shared/services/regSettingService';
import { getAllProfileGroups } from 'src/shared/services/profileGroupService';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { RegSettingDetail } from '../models/regSetting';

const OnboardingToggle = () => {
  const { t } = useTranslation(['settings']);
  const isMounted = useIsMounted();
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettings] = useState<RegSettingDetail[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getOnboardingSettings();
        const s = res.data;
        setSettings(s);
        const enabledSetting = s.find((x) => x.keyName === 'onboardingEnabled');
        setEnabled(enabledSetting?.bit === true);
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoaded(true);
      }
    };
    load();
  }, [isMounted]);

  const handleToggle = async (checked: boolean) => {
    setEnabled(checked);
    const enabledSetting = settings.find((x) => x.keyName === 'onboardingEnabled');
    const updates: RegSettingDetail[] = [];

    const enabledUpdate: RegSettingDetail = enabledSetting
      ? { ...enabledSetting, bit: checked }
      : {
          settings_id: 0,
          product_id: 0,
          category: 'onboarding',
          keyName: 'onboardingEnabled',
          field: 'bit',
          varchar: '',
          integer: 0,
          smallint: 0,
          bit: checked,
          text: '',
        };
    updates.push(enabledUpdate);

    // Nollställ stegen vid aktivering
    if (checked) {
      const stepsSetting = settings.find((x) => x.keyName === 'onboardingCompletedSteps');
      if (stepsSetting) {
        updates.push({ ...stepsSetting, varchar: '' });
      }
    }

    await updateOnboardingSettings(updates);
    const res = await getOnboardingSettings();
    setSettings(res.data);
  };

  if (!loaded) return null;

  return (
    <FormControlLabel
      control={<Switch checked={enabled} onChange={(_, checked) => handleToggle(checked)} />}
      label={t('settings:memberRegistry.onboardingEnabled')}
    />
  );
};

interface CustomColumn {
  index: number;
  name: string;
  heading: string;
  profileGroupId: number;
  profileItemId: number;
}

interface MemberRegistryForm {
  [key: string]: string | boolean;
  localLabel: string;
  localLabelPlural: string;
  countyLabel: string;
  countyLabelPlural: string;
  currency: string;
  assSelectorType: string;
  memberGridCustomCols: string;
  useOther: boolean;
  otherLabel: string;
  otherLabelPlural: string;
  sameAddress: boolean;
  memberGridHideDistrict: boolean;
  memberGridHideAssociation: boolean;
  subjectNewMember: string;
  bodyNewMember: string;
}

const KNOWN_FIELDS: { keyName: string; type: 'varchar' | 'bit' | 'text' }[] = [
  { keyName: 'localLabel', type: 'varchar' },
  { keyName: 'localLabelPlural', type: 'varchar' },
  { keyName: 'countyLabel', type: 'varchar' },
  { keyName: 'countyLabelPlural', type: 'varchar' },
  { keyName: 'currency', type: 'varchar' },
  { keyName: 'assSelectorType', type: 'varchar' },
  { keyName: 'memberGridCustomCols', type: 'varchar' },
  { keyName: 'useOther', type: 'bit' },
  { keyName: 'otherLabel', type: 'varchar' },
  { keyName: 'otherLabelPlural', type: 'varchar' },
  { keyName: 'sameAddress', type: 'bit' },
  { keyName: 'memberGridHideDistrict', type: 'bit' },
  { keyName: 'memberGridHideAssociation', type: 'bit' },
  { keyName: 'subjectNewMember', type: 'varchar' },
  { keyName: 'bodyNewMember', type: 'text' },
];

const mapSettingsToForm = (settings: RegSettingDetail[]): MemberRegistryForm => {
  const defaults: MemberRegistryForm = {
    localLabel: '',
    localLabelPlural: '',
    countyLabel: '',
    countyLabelPlural: '',
    currency: 'SEK',
    assSelectorType: '',
    memberGridCustomCols: '',
    useOther: false,
    otherLabel: '',
    otherLabelPlural: '',
    sameAddress: false,
    memberGridHideDistrict: false,
    memberGridHideAssociation: false,
    subjectNewMember: '',
    bodyNewMember: '',
  };

  settings.forEach((s) => {
    if (s.keyName in defaults) {
      if (s.field === 'bit') {
        defaults[s.keyName] = !!s.bit;
      } else if (s.field === 'text') {
        defaults[s.keyName] = s.text ?? '';
      } else {
        const val = s.varchar ?? '';
        defaults[s.keyName] = s.keyName === 'currency' && !val ? 'SEK' : val;
      }
    }
  });

  return defaults;
};

const mapFormToUpdates = (formData: MemberRegistryForm, settings: RegSettingDetail[]): RegSettingDetail[] => {
  const updates = settings.map((s) => {
    const knownField = KNOWN_FIELDS.find((f) => f.keyName === s.keyName);
    if (knownField && s.keyName in formData) {
      return {
        ...s,
        varchar: knownField.type === 'varchar' ? (formData[s.keyName] as string) : s.varchar,
        bit: knownField.type === 'bit' ? (formData[s.keyName] as boolean) : s.bit,
        text: knownField.type === 'text' ? (formData[s.keyName] as string) : s.text,
      };
    }
    return s;
  });

  // Skapa nya rader för fält som saknas i databasen
  KNOWN_FIELDS.forEach((kf) => {
    if (!settings.some((s) => s.keyName === kf.keyName)) {
      updates.push({
        // eslint-disable-next-line camelcase
        settings_id: 0,
        // eslint-disable-next-line camelcase
        product_id: 0,
        category: 'main',
        keyName: kf.keyName,
        field: kf.type,
        varchar: kf.type === 'varchar' ? (formData[kf.keyName] as string) : null,
        integer: 0,
        smallint: 0,
        bit: kf.type === 'bit' ? (formData[kf.keyName] as boolean) : null,
        text: kf.type === 'text' ? (formData[kf.keyName] as string) : null,
      });
    }
  });

  return updates;
};

const MEMBER_FIXED_FIELDS = [
  'email',
  'phone',
  'phoneOther',
  'birthDate',
  'address',
  'addressOther',
  'COAddress',
  'postNr',
  'postAddress',
  'country',
  'comment',
  'note',
  'admDate',
  'resDate',
  'custom1',
  'custom2',
  'custom3',
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
];

const isProfileColumn = (col: CustomColumn) => col.profileGroupId > 0 || col.name.startsWith('customProfile');

interface BaseColumn {
  label: string;
  hideFormField?: string;
}

type MergedColumn = { type: 'base'; column: BaseColumn } | { type: 'custom'; column: CustomColumn; arrayIndex: number };

const buildMergedColumns = (baseColumns: BaseColumn[], customs: CustomColumn[]): MergedColumn[] => {
  const merged: MergedColumn[] = baseColumns.map((column) => ({ type: 'base' as const, column }));
  customs.forEach((col, arrayIndex) => {
    const insertAt = Math.min(col.index, merged.length);
    merged.splice(insertAt, 0, { type: 'custom' as const, column: col, arrayIndex });
  });
  return merged;
};

const parseColumns = (json: string): CustomColumn[] => {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const MemberRegistrySettingsPage = () => {
  const { t } = useTranslation(['settings', 'common']);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<RegSettingDetail[]>([]);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [profileGroups, setProfileGroups] = useState<ProfileGroup[]>([]);

  const currencyOptions = [
    { value: 'SEK', label: 'SEK' },
    { value: 'EUR', label: 'EUR' },
  ];

  const assSelectorOptions = [
    { value: 'local', label: t('settings:memberRegistry.assSelectorOptions.local') },
    { value: 'link', label: t('settings:memberRegistry.assSelectorOptions.link') },
    { value: 'opt', label: t('settings:memberRegistry.assSelectorOptions.opt') },
  ];

  const formMethods = useForm<MemberRegistryForm>({
    mode: 'all',
    defaultValues: {
      localLabel: '',
      localLabelPlural: '',
      countyLabel: '',
      countyLabelPlural: '',
      currency: 'SEK',
      assSelectorType: '',
      memberGridCustomCols: '',
      useOther: false,
      otherLabel: '',
      otherLabelPlural: '',
      sameAddress: false,
      memberGridHideDistrict: false,
      memberGridHideAssociation: false,
      subjectNewMember: '',
      bodyNewMember: '',
    },
  });

  const { handleSubmit, reset, setValue, watch } = formMethods;

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsResult, profileGroupsResult] = await Promise.all([getMainSettings(), getAllProfileGroups(false, true)]);
      if (isMounted()) {
        setSettings(settingsResult.data);
        setProfileGroups(profileGroupsResult.data.results);
        const formValues = mapSettingsToForm(settingsResult.data);
        reset(formValues);
        setCustomColumns(parseColumns(formValues.memberGridCustomCols as string));
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, reset]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateColumnsJson = useCallback(
    (cols: CustomColumn[]) => {
      setCustomColumns(cols);
      setValue('memberGridCustomCols', cols.length > 0 ? JSON.stringify(cols) : '', { shouldDirty: true });
    },
    [setValue],
  );

  const baseColumns: BaseColumn[] = [
    { label: t('settings:memberRegistry.baseColumns.memberNr') },
    { label: t('settings:memberRegistry.baseColumns.firstName') },
    { label: t('settings:memberRegistry.baseColumns.lastName') },
    { label: t('settings:memberRegistry.baseColumns.district'), hideFormField: 'memberGridHideDistrict' },
    { label: t('settings:memberRegistry.baseColumns.association'), hideFormField: 'memberGridHideAssociation' },
    { label: t('settings:memberRegistry.baseColumns.status') },
    { label: t('settings:memberRegistry.baseColumns.icons') },
  ];

  const mergedColumns = buildMergedColumns(baseColumns, customColumns);

  const handleAddColumn = () => {
    const newIndex = baseColumns.length + customColumns.length;
    updateColumnsJson([...customColumns, { index: newIndex, name: '', heading: '', profileGroupId: 0, profileItemId: 0 }]);
  };

  const handleRemoveColumn = (arrayIdx: number) => {
    updateColumnsJson(customColumns.filter((_, i) => i !== arrayIdx));
  };

  const handleColumnChange = (arrayIdx: number, field: keyof CustomColumn, value: string) => {
    const updated = customColumns.map((col, i) => {
      if (i !== arrayIdx) return col;
      if (field === 'index' || field === 'profileGroupId' || field === 'profileItemId') return { ...col, [field]: parseInt(value, 10) || 0 };
      return { ...col, [field]: value };
    });
    updateColumnsJson(updated);
  };

  const handleMoveColumn = (arrayIdx: number, direction: 'up' | 'down') => {
    const updated = customColumns.map((col, i) => {
      if (i !== arrayIdx) return col;
      const newIndex = direction === 'up' ? Math.max(0, col.index - 1) : col.index + 1;
      return { ...col, index: newIndex };
    });
    updateColumnsJson(updated);
  };

  const handleColumnTypeChange = (arrayIdx: number, type: 'fixed' | 'profile') => {
    const updated = customColumns.map((col, i) => {
      if (i !== arrayIdx) return col;
      if (type === 'fixed') {
        return { ...col, name: '', profileGroupId: 0, profileItemId: 0 };
      }
      return { ...col, name: `customProfile${col.index}`, profileGroupId: 0, profileItemId: 0 };
    });
    updateColumnsJson(updated);
  };

  const handleFixedFieldChange = (arrayIdx: number, fieldName: string) => {
    const updated = customColumns.map((col, i) => (i === arrayIdx ? { ...col, name: fieldName } : col));
    updateColumnsJson(updated);
  };

  const handleProfileGroupChange = (arrayIdx: number, profileGroupId: number) => {
    const updated = customColumns.map((col, i) => {
      if (i !== arrayIdx) return col;
      return { ...col, profileGroupId, name: `customProfile${col.index}` };
    });
    updateColumnsJson(updated);
  };

  const onSubmit = useCallback(
    async (formData: MemberRegistryForm) => {
      setSaving(true);
      try {
        const updates = mapFormToUpdates(formData, settings);
        await updateMainSettings(updates);
        if (isMounted()) {
          toast.success(t('settings:memberRegistry.saveSuccess') as string);
          fetchSettings();
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          toast.error(t('settings:memberRegistry.saveError') as string);
        }
      } finally {
        if (isMounted()) setSaving(false);
      }
    },
    [isMounted, settings, t, fetchSettings],
  );

  return (
    <Box sx={{ position: 'relative', minHeight: loading ? 200 : 'auto' }}>
      <LoadingOverlay loading={loading} />
      {!loading && (
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Alert severity='warning'>{t('settings:memberRegistry.settingsWarning')}</Alert>
            <Grid container sx={{ p: 3 }}>
              {/* Kolumner */}
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('settings:memberRegistry.sectionColumns')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('settings:memberRegistry.sectionColumnsDescription')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box display='flex' justifyContent='flex-end' mb={1}>
                    <IconButton size='small' onClick={handleAddColumn} color='primary'>
                      <Add />
                    </IconButton>
                  </Box>
                  {/* eslint-disable react/no-array-index-key */}
                  {mergedColumns.map((item, mergedIdx) =>
                    item.type === 'base' ? (
                      <Box
                        key={`base-${mergedIdx}`}
                        display='flex'
                        alignItems='center'
                        gap={1}
                        mb={0.5}
                        px={1}
                        py={0.5}
                        sx={{
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          opacity: item.column.hideFormField && watch(item.column.hideFormField as string) ? 0.5 : 1,
                        }}
                      >
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{
                            flex: 1,
                            textDecoration: item.column.hideFormField && watch(item.column.hideFormField as string) ? 'line-through' : 'none',
                          }}
                        >
                          {item.column.label}
                        </Typography>
                        {item.column.hideFormField != null && (
                          <IconButton
                            size='small'
                            onClick={() => {
                              const field = item.column.hideFormField as string;
                              setValue(field, !watch(field), { shouldDirty: true });
                            }}
                          >
                            {watch(item.column.hideFormField as string) ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                          </IconButton>
                        )}
                        <Typography variant='caption' color='text.disabled'>
                          {t('settings:memberRegistry.columns.baseColumn')}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        key={`custom-${item.arrayIndex}`}
                        display='flex'
                        gap={1}
                        mb={0.5}
                        px={1}
                        py={0.5}
                        alignItems='center'
                        flexWrap='wrap'
                        sx={{ bgcolor: 'primary.50', border: 1, borderColor: 'primary.200', borderRadius: 1 }}
                      >
                        <Box display='flex' flexDirection='column'>
                          <IconButton size='small' onClick={() => handleMoveColumn(item.arrayIndex, 'up')} disabled={item.column.index <= 0}>
                            <ArrowUpward fontSize='inherit' />
                          </IconButton>
                          <IconButton size='small' onClick={() => handleMoveColumn(item.arrayIndex, 'down')}>
                            <ArrowDownward fontSize='inherit' />
                          </IconButton>
                        </Box>
                        <TextField
                          size='small'
                          label={t('settings:memberRegistry.columns.heading')}
                          value={item.column.heading}
                          onChange={(e) => handleColumnChange(item.arrayIndex, 'heading', e.target.value)}
                          sx={{ flex: 1, minWidth: 120 }}
                        />
                        <FormControl size='small' sx={{ minWidth: 140 }}>
                          <InputLabel>{t('settings:memberRegistry.columns.type')}</InputLabel>
                          <Select
                            value={isProfileColumn(item.column) ? 'profile' : 'fixed'}
                            label={t('settings:memberRegistry.columns.type')}
                            onChange={(e) => handleColumnTypeChange(item.arrayIndex, e.target.value as 'fixed' | 'profile')}
                          >
                            <MenuItem value='fixed'>{t('settings:memberRegistry.columns.typeFixed')}</MenuItem>
                            <MenuItem value='profile'>{t('settings:memberRegistry.columns.typeProfile')}</MenuItem>
                          </Select>
                        </FormControl>
                        {isProfileColumn(item.column) ? (
                          <FormControl size='small' sx={{ flex: 1, minWidth: 150 }}>
                            <InputLabel>{t('settings:memberRegistry.columns.profileGroup')}</InputLabel>
                            <Select
                              value={item.column.profileGroupId || ''}
                              label={t('settings:memberRegistry.columns.profileGroup')}
                              onChange={(e) => handleProfileGroupChange(item.arrayIndex, Number(e.target.value))}
                            >
                              {profileGroups.map((pg) => (
                                <MenuItem key={pg.memberProfile_id} value={pg.memberProfile_id}>
                                  {pg.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size='small' sx={{ flex: 1, minWidth: 150 }}>
                            <InputLabel>{t('settings:memberRegistry.columns.fixedField')}</InputLabel>
                            <Select
                              value={item.column.name}
                              label={t('settings:memberRegistry.columns.fixedField')}
                              onChange={(e) => handleFixedFieldChange(item.arrayIndex, e.target.value)}
                            >
                              {MEMBER_FIXED_FIELDS.map((f) => (
                                <MenuItem key={f} value={f}>
                                  {t(`settings:memberRegistry.memberFields.${f}`, f)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <IconButton size='small' onClick={() => handleRemoveColumn(item.arrayIndex)} color='error'>
                          <Delete />
                        </IconButton>
                      </Box>
                    ),
                  )}
                  {/* eslint-enable react/no-array-index-key */}
                  {customColumns.length === 0 && (
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                      {t('settings:memberRegistry.columns.empty')}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Divider />
              </Grid>

              {/* E-post till ny medlem */}
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('settings:memberRegistry.sectionNewMemberEmail')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('settings:memberRegistry.sectionNewMemberEmailDescription')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ControlledTextField
                    name='subjectNewMember'
                    label={t('settings:memberRegistry.fields.subjectNewMember')}
                    size='small'
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <ControlledTextEditor name='bodyNewMember' rows={25} allowedDataFields={[DataFieldsEnum.FirstName, DataFieldsEnum.LastName]} />
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Divider />
              </Grid>

              {/* Benämningar och väljare */}
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('settings:memberRegistry.sectionLabelsAndSelectors')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('settings:memberRegistry.sectionLabelsAndSelectorsDescription')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <ControlledTextField name='localLabel' label={t('settings:memberRegistry.fields.localLabel')} size='small' fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ControlledTextField
                        name='localLabelPlural'
                        label={t('settings:memberRegistry.fields.localLabelPlural')}
                        size='small'
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ControlledTextField name='countyLabel' label={t('settings:memberRegistry.fields.countyLabel')} size='small' fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ControlledTextField
                        name='countyLabelPlural'
                        label={t('settings:memberRegistry.fields.countyLabelPlural')}
                        size='small'
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ControlledSelectBox name='currency' label={t('settings:memberRegistry.fields.currency')} items={currencyOptions} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ControlledSelectBox
                        name='assSelectorType'
                        label={t('settings:memberRegistry.fields.assSelectorType')}
                        items={assSelectorOptions}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledCheckboxNew name='useOther' label={t('settings:memberRegistry.fields.useOther')} />
                    </Grid>
                    {watch('useOther') && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <ControlledTextField name='otherLabel' label={t('settings:memberRegistry.fields.otherLabel')} size='small' fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <ControlledTextField
                            name='otherLabelPlural'
                            label={t('settings:memberRegistry.fields.otherLabelPlural')}
                            size='small'
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <ControlledCheckboxNew name='sameAddress' label={t('settings:memberRegistry.fields.sameAddress')} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button type='submit' variant='contained' disabled={saving}>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      )}

      {/* Uppstartsguide */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant='subtitle1' fontWeight='bold'>
              {t('settings:memberRegistry.sectionOnboarding')}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {t('settings:memberRegistry.sectionOnboardingDescription')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <OnboardingToggle />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
